import { Platform } from '@angular/cdk/platform';
import { AfterViewInit } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DateAdapter, MatDateFormats, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatCalendar, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Subject, takeUntil } from 'rxjs';
import { InputDateMonthAdapter } from './input-date-month-adapter';

@Component({
  selector: 'input-date-month',
  templateUrl: './input-date-month.component.html',
  styleUrls: ['./input-date-month.component.css'],
  providers: [
    {
      provide: DateAdapter,
      useClass: InputDateMonthAdapter,
      deps: [MAT_DATE_LOCALE, Platform],
    },
  ],
})
export class InputDateMonthComponent  implements AfterViewInit  {

  inputDateMonthHeader = InputDateMonthHeaderComponent;


  @Input() control: FormControl;
  @Input() id: string = '';
  @Input() title: string = '';
  @Input() placeholder: string = '';
  @Input() minDateJson: any = {};
  @Input() maxDateJson: any = {};
  @Input() dates: Array<number> = [];
  @Input() minDate: Date = new Date();
  @Input() maxDate: Date | undefined = new Date();
  @Input() minYear: number = -1;
  @Input() maxYear: number = -1;
  @Input() startView: "month" | "year" | "multi-year" = 'year';
  @Input() messageRequired: string = 'Información requerida';
  @Input() messageFooter: string = '';

  @Output() changeValuesEvent = new EventEmitter<undefined>();

  constructor() {

    this.minDate.setHours(0, 0, 0, 0);
    this.maxDate.setHours(0, 0, 0, 0);

    this.minDate.setUTCFullYear(this.minDate.getUTCFullYear());
    this.minDateJson = { year: this.minDate.getUTCFullYear(), month: this.minDate.getUTCMonth() + 1, day: this.minDate.getDate() };
    if (this.maxDate !== undefined) {
      this.maxDate.setUTCFullYear(this.maxDate.getUTCFullYear() + 100);
      this.maxDateJson = { year: this.maxDate.getUTCFullYear(), month: this.maxDate.getUTCMonth() + 1, day: this.maxDate.getDate() };
    }
  }

  ngAfterViewInit() {
    this.control.addValidators(this.validateDate());
  }

  dayFilter = (d: Date | null | undefined | any): boolean => {

    if (this.dates.length === 0)
      return true;

    const date = d !== null && d !== undefined ? d.toDate() : new Date();

    return this.dates.filter(item => item === date.getTime()).length > 0 ? true : false;

  };

  dateChangeEvent(event: MatDatepickerInputEvent<Date>) {
    this.changeValuesEvent.next(undefined);
  }

  public monthChanged(value: any, widget: any): void {
    console.log(value);
    this.control.setValue(value)
    //this.monthAndYear = value;
    widget.close();
  }

  validateDate(): ValidatorFn | ValidatorFn[] {

    return (): ValidationErrors | null => {

      if (!this.control.value)
        return null;

      const currentValue: Date = new Date(this.control.value);

      currentValue.setHours(0, 0, 0, 0);

      if (this.minYear > -1 && this.maxYear > -1) {

        if (this.maxDate !== undefined && currentValue > this.maxDate) {
          return { invalidDate: { minyear: { min: this.minYear } } };
        }

        if (currentValue < this.minDate) {
          return { invalidDate: { maxyear: { max: this.maxYear } } };
        }
        return null;

      }

      if (this.maxDate === undefined) {
        if (currentValue < this.minDate) {
          return { invalidDate: { mindate: { current: currentValue, min: this.minDate } } };
        }
      }
      else {
        if (currentValue < this.minDate || currentValue > this.maxDate) {
          return { invalidDate: { range: { current: currentValue, min: this.minDate, max: this.maxDate } } };
        }
      }
    }
  }
}

/** Custom header component for datepicker. */
@Component({
  selector: 'input-date-month-header',
  styles: [
    `
    .header-class {
      display: flex;
      align-items: center;
      padding: 0.5em;
    }

    .example-header-label {
      flex: 1;
      height: 1.5em;
      font-weight: 500;
      text-align: center;
    }
 
  `,
  ],
  template: `
    <div class="header-class">
    <button class="btn" mat-icon-button  (click)="previousClicked('year')">
        <mat-icon>keyboard_arrow_left</mat-icon>
      </button>
      <span class="example-header-label">{{periodLabel}}</span>
      <button class="btn" mat-icon-button  (click)="nextClicked('year')">
        <mat-icon>keyboard_arrow_right</mat-icon>
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputDateMonthHeaderComponent<D> implements OnDestroy {
  private _destroyed = new Subject<void>();

  constructor(
    private _calendar: MatCalendar<D>,
    private _dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
    cdr: ChangeDetectorRef,
  ) {
    _calendar.stateChanges.pipe(takeUntil(this._destroyed)).subscribe(() => cdr.markForCheck());
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  get periodLabel() {
    return this._dateAdapter
      .format(this._calendar.activeDate, this._dateFormats.display.monthYearA11yLabel)
      .toLocaleUpperCase();
  }

  previousClicked(mode: 'month' | 'year') {
    this._calendar.activeDate =
      mode === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, -1);
  }

  nextClicked(mode: 'month' | 'year') {
    this._calendar.activeDate =
      mode === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, 1);
  }
}